import Box from '@mui/material/Box';
import { AccessForbiddenPage } from '@openx/components-core/lib/AccessForbiddenBackPage/AccessForbiddenPage';
import { Button } from '@openx/components-core/lib/Button/Button';
import { DealPrompt } from '@openx/components-deal-prompt/lib/DealPrompt';
import { DealPromptDetails } from '@openx/components-deal-prompt/lib/DealPromptDetails';
import { useDealPrompt } from '@openx/components-deal-prompt/lib/hooks/useDealPrompt';
import { useDealPromptCreateDeal } from '@openx/components-deal-prompt/lib/hooks/useDealPromptCreateDeal';
import { useDealPromptPermission } from '@openx/components-deal-prompt/lib/hooks/useDealPromptPermission';
import { DealPromptAudienceCreateDealDialog } from '@openx/components-deal-prompt/lib/parts/DealPromptAudienceCreateDealDialog';
import { DealPromptDetailsDownloadMenu } from '@openx/components-deal-prompt/lib/parts/details/header/DealPromptDetailsDownloadMenu';
import { DealPromptDetailsExportButton } from '@openx/components-deal-prompt/lib/parts/details/header/DealPromptDetailsExportButton';
import { checkExportEligibility } from '@openx/components-deal-prompt/lib/utils/checkAudienceExportEligibility';
import { DealPromptProvider } from '@openx/components-deal-prompt/lib/utils/context/DealPromptContext';
import { getDealPromptAudienceDealTargetAccount } from '@openx/components-deal-prompt/lib/utils/getDealPromptAudienceDealTargetAccount';
import { toAudienceSegmentSet } from '@openx/components-deal-prompt/lib/utils/getTilesData';
import { isExplicitNonUsRegion } from '@openx/components-deal-prompt/lib/utils/isExplicitNonUsRegion';
import { createNormalizedDefinitionFromSegmentSetAndSelection } from '@openx/components-deal-prompt/lib/utils/normalizedDefinitionFromAudienceItems';
import {
  type DealPromptAudienceDealFormValues,
  dealPromptContextTypes,
} from '@openx/components-deal-prompt/lib/utils/types';
import { PageHeader } from '@openx/components-header/PageHeader';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../components/AuthContext/context';
import { ViewOptions } from './consts';

export const DealPromptPageContent = () => {
  const { session } = useAuth();
  const {
    state: { activeViewType: view, selectedTile, selectedItems, selectedAudienceItems, contextType },
    handleBack,
  } = useDealPrompt();

  const { isAudienceCreateDealOpen, handleCreateDeal, closeAudienceCreateDealDialog, submitAudienceCreateDeal } =
    useDealPromptCreateDeal();
  const { t } = useTranslation();

  const isDetails = view === ViewOptions.DETAILS && !!selectedTile;

  const audienceDealTargetFromSelection = useMemo(
    () => getDealPromptAudienceDealTargetAccount(selectedAudienceItems, session?.account?.id),
    [selectedAudienceItems, session?.account?.id]
  );

  const isCreateDealDisabled = useMemo(() => {
    if (contextType === dealPromptContextTypes.AUDIENCE) {
      return !checkExportEligibility(selectedAudienceItems);
    }
    return !selectedItems || selectedItems.length === 0;
  }, [contextType, selectedAudienceItems, selectedItems]);

  const isAudienceSelectionExplicitNonUsRegion = useMemo(() => {
    if (contextType !== dealPromptContextTypes.AUDIENCE || !selectedTile?.item || !('segments' in selectedTile.item)) {
      return false;
    }

    return isExplicitNonUsRegion(
      createNormalizedDefinitionFromSegmentSetAndSelection(selectedTile.item, selectedAudienceItems)
    );
  }, [contextType, selectedTile, selectedAudienceItems]);

  const handleSubmitAudienceCreateDeal = useCallback(
    async (form: DealPromptAudienceDealFormValues) => {
      if (selectedTile && 'item' in selectedTile) {
        const audienceItem = selectedTile.item;

        if (audienceItem && 'segments' in audienceItem) {
          await submitAudienceCreateDeal({
            form,
            segmentSet: toAudienceSegmentSet(audienceItem),
            selectedSegments: selectedAudienceItems,
            ...(audienceDealTargetFromSelection
              ? {
                  audienceAccountIdOverride: audienceDealTargetFromSelection.audienceAccountId,
                  dealFormAccountUidOverride: audienceDealTargetFromSelection.dealFormAccountUid,
                }
              : {}),
          });
        }
      }
    },
    [selectedTile, submitAudienceCreateDeal, selectedAudienceItems, audienceDealTargetFromSelection]
  );

  const pageTitle = useMemo(() => {
    if (isDetails && contextType !== dealPromptContextTypes.AUDIENCE) {
      return t('{title} Set', { title: selectedTile?.title });
    }
    return t('Deal Prompt');
  }, [isDetails, contextType, selectedTile?.title, t]);

  return (
    <PageHeader
      titlePrefix="OpenXSelect"
      title={pageTitle}
      goBack={isDetails ? handleBack : undefined}
      customGoBackText={t('Back to results')}
      render={() =>
        isDetails ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '40%', justifyContent: 'flex-end' }}>
            <Button
              color="primary"
              data-test="create-deal-from-details"
              data-pendo-feature={`deal-prompt-details-${contextType}-create-deal`}
              disabled={isCreateDealDisabled}
              onClick={() => selectedTile?.item && handleCreateDeal(selectedTile.item)}
            >
              {t('Create deal')}
            </Button>
            {contextType === dealPromptContextTypes.AUDIENCE ? (
              <DealPromptDetailsExportButton />
            ) : (
              <DealPromptDetailsDownloadMenu data-test="download-results" />
            )}
          </Box>
        ) : null
      }
    >
      {contextType === dealPromptContextTypes.AUDIENCE && (
        <DealPromptAudienceCreateDealDialog
          open={isAudienceCreateDealOpen}
          onClose={closeAudienceCreateDealDialog}
          isExplicitNonUsRegion={isAudienceSelectionExplicitNonUsRegion}
          dataPartnerAccountName={
            audienceDealTargetFromSelection ? audienceDealTargetFromSelection.accountName : undefined
          }
          onSubmit={handleSubmitAudienceCreateDeal}
        />
      )}

      {!isDetails && <DealPrompt />}

      {isDetails && <DealPromptDetails />}
    </PageHeader>
  );
};

export const DealPromptPage = () => {
  const { hasDealPrompt } = useDealPromptPermission();

  if (!hasDealPrompt) {
    return <AccessForbiddenPage />;
  }

  return (
    <DealPromptProvider>
      <DealPromptPageContent />
    </DealPromptProvider>
  );
};
